// src/composables/useNotifications.js
//
// Shared in-app notification state (navbar bell + "All Notifications" page).
// Module-level singleton refs on purpose: every component that calls
// useNotifications() must see the SAME list/unread count, and there must
// only ever be one socket.io connection per browser tab, not one per
// mounted component.
import { ref } from '@vue/composition-api'
import Vue from 'vue'
import axios from 'axios'
import apiEndpoints from '@/constants/apiEndpoints'
import { connectSocket } from '@/libs/socket'

const notifications = ref([])
const unreadCount = ref(0)
const loading = ref(false)
let initialized = false
let fallbackPoller = null

function authHeaders() {
  const userToken = Vue.$cookies.get('userToken')
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${userToken}`,
  }
}

function apiUrl(path) {
  return `${process.env.VUE_APP_API_URL}${path}`
}

// Simple generic two-tone "ding" via the Web Audio API - deliberately not a
// bundled audio file (client requirement was "sound generik simpel", and
// this avoids needing to host/ship an asset just for a beep). Wrapped so a
// browser autoplay-policy rejection or missing AudioContext can never break
// notification delivery itself.
function playNotificationSound() {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    if (!AudioContextClass) return
    const ctx = new AudioContextClass()
    const now = ctx.currentTime

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.setValueAtTime(880, now)
    osc.frequency.exponentialRampToValueAtTime(660, now + 0.15)
    gain.gain.setValueAtTime(0.0001, now)
    gain.gain.exponentialRampToValueAtTime(0.18, now + 0.01)
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.35)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(now)
    osc.stop(now + 0.35)
    osc.onended = () => ctx.close()
  } catch (e) {
    // Autoplay-blocked or unsupported - never let a sound failure break
    // notification delivery.
  }
}

function wireSocket() {
  const socket = connectSocket()
  if (!socket) return
  // Guard against double-registration if init() is somehow called twice.
  socket.off('notification:new')
  socket.on('notification:new', notification => {
    notifications.value = [notification, ...notifications.value]
    unreadCount.value += 1
    playNotificationSound()
  })
}

function startFallbackPolling(fetchUnreadCount, fetchNotifications) {
  fetchUnreadCount()
  fetchNotifications({ limit: 5 }).catch(() => {})
  if (fallbackPoller) return
  fallbackPoller = window.setInterval(() => {
    fetchUnreadCount()
    fetchNotifications({ limit: 5 }).catch(() => {})
  }, 30000)
}

export default function useNotifications() {
  const fetchNotifications = async (params = {}, options = {}) => {
    const shouldUpdateStore = options.updateStore !== false
    if (shouldUpdateStore) loading.value = true
    try {
      const { data } = await axios.get(apiUrl(apiEndpoints.API_GET_NOTIFICATIONS), {
        headers: authHeaders(),
        params,
      })
      if (shouldUpdateStore) {
        notifications.value = data.data || []
      }
      return data
    } finally {
      if (shouldUpdateStore) loading.value = false
    }
  }

  const fetchUnreadCount = async () => {
    try {
      const { data } = await axios.get(apiUrl(apiEndpoints.API_GET_NOTIFICATIONS_UNREAD_COUNT), {
        headers: authHeaders(),
      })
      unreadCount.value = (data.data && data.data.count) || 0
    } catch (e) {
      // Non-fatal - badge just won't update this cycle.
    }
  }

  const markRead = async id => {
    await axios.post(apiUrl(`${apiEndpoints.API_MARK_NOTIFICATION_READ}${id}/read`), {}, { headers: authHeaders() })
    const n = notifications.value.find(x => x.notification_id === id)
    if (n && !n.notification_is_read) {
      n.notification_is_read = true
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    }
  }

  const markUnread = async id => {
    await axios.post(apiUrl(`${apiEndpoints.API_MARK_NOTIFICATION_UNREAD}${id}/unread`), {}, { headers: authHeaders() })
    const n = notifications.value.find(x => x.notification_id === id)
    if (n && n.notification_is_read) {
      n.notification_is_read = false
      unreadCount.value += 1
    }
  }

  const markAllRead = async () => {
    await axios.post(apiUrl(apiEndpoints.API_MARK_ALL_NOTIFICATIONS_READ), {}, { headers: authHeaders() })
    notifications.value.forEach(n => { n.notification_is_read = true })
    unreadCount.value = 0
  }

  const markAllUnread = async () => {
    await axios.post(apiUrl(apiEndpoints.API_MARK_ALL_NOTIFICATIONS_UNREAD), {}, { headers: authHeaders() })
    notifications.value.forEach(n => { n.notification_is_read = false })
    unreadCount.value = notifications.value.length
  }

  const archive = async id => {
    await axios.post(apiUrl(`${apiEndpoints.API_ARCHIVE_NOTIFICATION}${id}/archive`), {}, { headers: authHeaders() })
    const n = notifications.value.find(x => x.notification_id === id)
    if (n && !n.notification_is_read) {
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    }
    notifications.value = notifications.value.filter(x => x.notification_id !== id)
  }

  const unarchive = async id => {
    await axios.post(apiUrl(`${apiEndpoints.API_UNARCHIVE_NOTIFICATION}${id}/unarchive`), {}, { headers: authHeaders() })
    const n = notifications.value.find(x => x.notification_id === id)
    if (n && !n.notification_is_read) {
      unreadCount.value += 1
    }
    notifications.value = notifications.value.filter(x => x.notification_id !== id)
  }

  const remove = async id => {
    await axios.post(apiUrl(`${apiEndpoints.API_DELETE_NOTIFICATION}${id}/delete`), {}, { headers: authHeaders() })
    const n = notifications.value.find(x => x.notification_id === id)
    if (n && !n.notification_is_read) {
      unreadCount.value = Math.max(0, unreadCount.value - 1)
    }
    notifications.value = notifications.value.filter(x => x.notification_id !== id)
  }

  // Called once from the app shell (Vertical/Horizontal layout) after
  // login - connects the socket and primes the unread badge. Safe to call
  // from multiple components; only runs once per page load.
  const init = () => {
    if (initialized) return
    initialized = true
    const enableSocket = process.env.VUE_APP_ENABLE_SOCKET_NOTIFICATIONS === 'true'
    if (enableSocket) {
      wireSocket()
      fetchUnreadCount()
      return
    }
    startFallbackPolling(fetchUnreadCount, fetchNotifications)
  }

  return {
    notifications,
    unreadCount,
    loading,
    init,
    fetchNotifications,
    fetchUnreadCount,
    markRead,
    markUnread,
    markAllRead,
    markAllUnread,
    archive,
    unarchive,
    remove,
  }
}
